import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import {
  HrCountryPack,
  HrCountryPackDocumentRequirement,
  HrCountryPackExportFormat,
  HrCountryPackStatutoryRule,
  HrEmployee,
  HrEmployeeComplianceProfile,
  HrEndOfServicePreview,
  HrEndOfServicePreviewRequest,
  HrPayrollRun,
  HrPayrollStatutoryOutput,
  HrStatutoryExportBatch,
  HrStatutoryExportRequest,
  HrTenantCountryPackSetting
} from '../hr.models';
import { HrService } from '../hr.service';
import { HrActionDialogComponent } from '../shared/hr-action-dialog.component';
import { HrListTabsComponent } from '../shared/hr-list-tabs.component';
import { HrModuleNavComponent } from '../shared/hr-module-nav.component';

interface SelectOption<T = string | number> {
  label: string;
  value: T;
}

@Component({
  selector: 'app-hr-country-packs',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputNumberModule,
    InputTextModule,
    SelectModule,
    TableModule,
    TagModule,
    TextareaModule,
    ToastModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent,
    PageFormSectionComponent,
    HrActionDialogComponent,
    HrListTabsComponent,
    HrModuleNavComponent
  ],
  providers: [MessageService],
  templateUrl: './hr-country-packs.component.html',
  styleUrls: ['./hr-country-packs.component.scss']
})
export class HrCountryPacksComponent implements OnInit {
  activeListTab = 'marketplace';
  activeDialog?: 'activation' | 'profile' | 'statutory' | 'export' | 'eos';
  private readonly hrService = inject(HrService);
  private readonly messageService = inject(MessageService);
  private readonly permissionService = inject(PermissionService);

  readonly canManagePacks = this.permissionService.hasPermission(app_permissions.hr.countryPacks.manage);
  readonly canManageProfiles = this.permissionService.hasPermission(app_permissions.hr.countryPacks.employeeManage);
  readonly canManageExports = this.permissionService.hasPermission(app_permissions.hr.countryPacks.exportsManage);

  readonly countryOptions: SelectOption[] = [
    { label: 'Saudi Arabia (SA)', value: 'SA' },
    { label: 'United Arab Emirates (AE)', value: 'AE' },
    { label: 'India (IN)', value: 'IN' },
    { label: 'Pakistan (PK)', value: 'PK' }
  ];

  packs: HrCountryPack[] = [];
  settings: HrTenantCountryPackSetting[] = [];
  requirements: HrCountryPackDocumentRequirement[] = [];
  rules: HrCountryPackStatutoryRule[] = [];
  exportFormats: HrCountryPackExportFormat[] = [];
  employees: HrEmployee[] = [];
  payrollRuns: HrPayrollRun[] = [];
  profiles: HrEmployeeComplianceProfile[] = [];
  outputs: HrPayrollStatutoryOutput[] = [];
  exportBatches: HrStatutoryExportBatch[] = [];
  eosPreviews: HrEndOfServicePreview[] = [];

  selectedCountry = 'SA';
  selectedPackVersionId?: number;

  settingForm = {
    countryCode: 'SA',
    countryPackVersionId: undefined as number | undefined,
    defaultRegionCode: '',
    payrollEnabled: true,
    complianceEnabled: true,
    exportsEnabled: true,
    notes: ''
  };

  profileForm: HrEmployeeComplianceProfile = {
    countryCode: 'SA',
    complianceProfileStatus: 'ACTIVE'
  };

  previewForm = {
    payrollRunId: undefined as number | undefined,
    countryCode: 'SA'
  };

  exportForm: HrStatutoryExportRequest = {
    countryCode: 'SA'
  };

  eosForm: HrEndOfServicePreviewRequest = {
    countryCode: 'SA',
    currencyCode: 'SAR'
  };

  ngOnInit(): void {
    this.loadAll();
  }

  get employeeOptions(): SelectOption<number>[] {
    return this.employees.map(employee => ({
      label: `${employee.displayName || 'Employee'}${employee.employeeNumber ? ` (${employee.employeeNumber})` : ''}`,
      value: employee.employeeId || 0
    })).filter(option => !!option.value);
  }

  get payrollRunOptions(): SelectOption<number>[] {
    return this.payrollRuns.map(run => ({
      label: `${run.runName || run.runCode || 'Payroll run'}${run.runStatus ? ` - ${run.runStatus}` : ''}`,
      value: run.payrollRunId || 0
    })).filter(option => !!option.value);
  }

  get versionOptions(): SelectOption<number>[] {
    return this.packs.flatMap(pack => (pack.versions || []).map(version => ({
      label: `${pack.countryCode} - ${pack.packName} / ${version.versionCode}`,
      value: version.countryPackVersionId || 0
    }))).filter(option => !!option.value);
  }

  get exportFormatOptions(): SelectOption<number>[] {
    return this.exportFormats.map(format => ({
      label: `${format.countryCode} - ${format.formatName}`,
      value: format.exportFormatId || 0
    })).filter(option => !!option.value);
  }

  get metrics(): PageMetric[] {
    return [
      { label: 'Country Packs', value: this.packs.length, tone: 'info' },
      { label: 'Active Settings', value: this.settings.filter(setting => setting.active).length, tone: 'success' },
      { label: 'Requirements', value: this.requirements.length, tone: 'default' },
      { label: 'Rules', value: this.rules.length, tone: 'warn' },
      { label: 'Exports', value: this.exportBatches.length, tone: 'info' }
    ];
  }

  loadAll(): void {
    forkJoin({
      packs: this.hrService.countryPacks(undefined, true),
      settings: this.hrService.tenantCountryPackSettings(undefined, undefined, true),
      requirements: this.hrService.countryPackDocumentRequirements(this.selectedCountry),
      rules: this.hrService.countryPackStatutoryRules(this.selectedCountry, undefined, true),
      formats: this.hrService.countryPackExportFormats(this.selectedCountry),
      employees: this.hrService.employees({ pageSize: 200 }),
      runs: this.hrService.payrollRuns(undefined, undefined, 50),
      profiles: this.hrService.employeeComplianceProfiles(undefined, undefined, this.selectedCountry),
      outputs: this.hrService.payrollStatutoryOutputs(undefined, undefined, this.selectedCountry),
      batches: this.hrService.statutoryExportBatches(undefined, this.selectedCountry, undefined, 25),
      eos: this.hrService.endOfServicePreviews(undefined, undefined, this.selectedCountry, 25)
    }).subscribe({
      next: result => {
        this.packs = result.packs.data || [];
        this.settings = result.settings.data || [];
        this.requirements = result.requirements.data || [];
        this.rules = result.rules.data || [];
        this.exportFormats = result.formats.data || [];
        this.employees = result.employees.items || [];
        this.payrollRuns = result.runs.data || [];
        this.profiles = result.profiles.data || [];
        this.outputs = result.outputs.data || [];
        this.exportBatches = result.batches.data || [];
        this.eosPreviews = result.eos.data || [];
        this.selectedPackVersionId = this.versionOptions[0]?.value;
        this.settingForm.countryPackVersionId ||= this.versionOptions[0]?.value;
        this.exportForm.exportFormatId ||= this.exportFormatOptions[0]?.value;
      },
      error: () => this.toast('error', 'Unable to load HR country pack data.')
    });
  }

  changeCountry(countryCode: string): void {
    this.selectedCountry = countryCode;
    this.settingForm.countryCode = countryCode;
    this.profileForm.countryCode = countryCode;
    this.previewForm.countryCode = countryCode;
    this.exportForm.countryCode = countryCode;
    this.eosForm.countryCode = countryCode;
    this.eosForm.currencyCode = countryCode === 'AE' ? 'AED' : countryCode === 'IN' ? 'INR' : countryCode === 'PK' ? 'PKR' : 'SAR';
    this.loadAll();
  }

  activateSelectedVersion(): void {
    if (!this.settingForm.countryPackVersionId || !this.canManagePacks) {
      return;
    }
    this.hrService.saveTenantCountryPackSetting({
      ...this.settingForm,
      active: true
    }).subscribe({
      next: () => {
        this.toast('success', 'Country pack setting saved.');
        this.activeDialog = undefined;
        this.loadAll();
      },
      error: () => this.toast('error', 'Country pack setting could not be saved.')
    });
  }

  saveProfile(): void {
    if (!this.profileForm.employeeId || !this.profileForm.countryCode || !this.canManageProfiles) {
      return;
    }
    this.hrService.saveEmployeeComplianceProfile(this.profileForm).subscribe({
      next: saved => {
        this.profileForm = { countryCode: this.selectedCountry, complianceProfileStatus: 'ACTIVE' };
        this.activeDialog = undefined;
        this.toast('success', saved.message || 'Employee compliance profile saved.');
        this.loadAll();
      },
      error: () => this.toast('error', 'Employee compliance profile could not be saved.')
    });
  }

  previewStatutoryOutputs(): void {
    if (!this.previewForm.payrollRunId || !this.canManagePacks) {
      return;
    }
    this.hrService.previewPayrollStatutoryOutputs(this.previewForm.payrollRunId, undefined, this.previewForm.countryCode).subscribe({
      next: response => {
        this.outputs = response.data || [];
        this.activeDialog = undefined;
        this.toast('success', 'Statutory payroll preview generated.');
      },
      error: () => this.toast('error', 'Statutory preview could not be generated.')
    });
  }

  generateExport(): void {
    if (!this.exportForm.countryCode || !this.canManageExports) {
      return;
    }
    this.hrService.generateStatutoryExport(this.exportForm).subscribe({
      next: () => {
        this.toast('success', 'Statutory export staged.');
        this.activeDialog = undefined;
        this.loadAll();
      },
      error: () => this.toast('error', 'Statutory export could not be staged.')
    });
  }

  previewEos(): void {
    if (!this.eosForm.employeeId || !this.eosForm.countryCode || !this.canManagePacks) {
      return;
    }
    this.hrService.previewEndOfService(this.eosForm).subscribe({
      next: response => {
        if (response.data) {
          this.eosPreviews = [response.data, ...this.eosPreviews];
        }
        this.toast('success', 'End-of-service preview generated.');
        this.activeDialog = undefined;
      },
      error: () => this.toast('error', 'End-of-service preview could not be generated.')
    });
  }

  severity(value?: string): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
    switch ((value || '').toUpperCase()) {
      case 'ACTIVE':
      case 'APPROVED':
      case 'STAGED':
      case 'PREVIEW':
        return 'success';
      case 'PENDING_REVIEW':
        return 'warn';
      case 'INACTIVE':
      case 'FAILED':
        return 'danger';
      default:
        return 'info';
    }
  }

  openActionDialog(dialog: NonNullable<HrCountryPacksComponent['activeDialog']>): void {
    this.activeDialog = dialog;
  }

  actionDialogVisible(dialog: NonNullable<HrCountryPacksComponent['activeDialog']>): boolean {
    return this.activeDialog === dialog;
  }

  setActionDialogVisible(dialog: NonNullable<HrCountryPacksComponent['activeDialog']>, visible: boolean): void {
    this.activeDialog = visible ? dialog : undefined;
  }

  private toast(severity: 'success' | 'error' | 'info', detail: string): void {
    this.messageService.add({ severity, summary: severity === 'error' ? 'Country Packs' : 'Saved', detail });
  }
}
